import { useCallback } from 'react';
import elementToImage from '@/components/Editor/share/ChartShare/elementToImage';
import { createImageCopyError } from './errors';
const PREFIX = 'components: Editor: share: hooks: useCopyImage:';
export const useCopyImage = _ref => {
  let {
    captureElement,
    pixelRatio: defaultPixelRatio,
    onLoadingChange,
    onImageCopy,
    onError
  } = _ref;
  const handleCopyImage = useCallback(async pixelRatio => {
    onLoadingChange === null || onLoadingChange === void 0 ? void 0 : onLoadingChange(true);

    try {
      const getImagePromise = async () => {
        if (!captureElement.current) {
          throw new Error(`${PREFIX} captureElement is not set`);
        }

        const dataUrl = await elementToImage({
          fileType: 'png',
          // png is the only supported format for clipboard
          captureElement: captureElement.current,
          pixelRatio: pixelRatio ?? defaultPixelRatio
        }); // convert the data URL to a blob

        const response = await fetch(dataUrl);
        return await response.blob();
      };

      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const isNotFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') === -1;

      if (isSafari) {
        // Safari treats user activation differently:
        // https://bugs.webkit.org/show_bug.cgi?id=222262
        await navigator.clipboard.write([new ClipboardItem({
          'image/png': getImagePromise()
        })]);
      } else if (isNotFirefox) {
        // For chrome and edge, we need to check for permissions
        const permissionsResult = await navigator.permissions.query({
          name: 'clipboard-write'
        });
        const hasPermission = permissionsResult.state === 'granted' || permissionsResult.state === 'prompt';

        if (!hasPermission) {
          throw new Error(`${PREFIX} clipboard-write permission denied`);
        }

        const blob = await getImagePromise();
        await navigator.clipboard.write([new ClipboardItem({
          [blob.type]: blob
        })]);
      } else {
        // Firefox doesn't support permissions API for "clipboard-write"
        const blob = await getImagePromise();
        await navigator.clipboard.write([new ClipboardItem({
          [blob.type]: blob
        })]);
      }

      onImageCopy === null || onImageCopy === void 0 ? void 0 : onImageCopy();
    } catch (err) {
      onError === null || onError === void 0 ? void 0 : onError(createImageCopyError(`${PREFIX} elementToImage: failed: ${err}`, err));
    } finally {
      onLoadingChange === null || onLoadingChange === void 0 ? void 0 : onLoadingChange(false);
    }
  }, [onError, onImageCopy, onLoadingChange, captureElement, defaultPixelRatio]);
  return {
    handleCopyImage
  };
};